import * as Sentry from "@sentry/vue"
import {browserTracingIntegration, ErrorEvent, EventHint} from "@sentry/vue"
import Vue from "vue"
import axios, {AxiosError} from "axios"

const TRACE_IGNORE = [
  /\/_/,
  /\/student\/\d+\/track$/,
]

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'local'
  const release = import.meta.env.VITE_SENTRY_RELEASE ?? 'dev-master';

  if (dsn) {
    Sentry.init({
      Vue,
      dsn,
      environment,
      release,
      logErrors: true,
      tracesSampleRate: 0.1,
      integrations: [browserTracingIntegration({shouldCreateSpanForRequest})],
      beforeSend,
    })
    addAxiosSentryInterceptor()
    console.info("Sentry initialized", {dsn, environment, release})
  } else {
    console.warn("VITE_SENTRY_DSN not defined -- Sentry integration disabled")
  }
}

function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  // console.log("[Sentry] beforeSend", {event, hint})

  if (!shouldCaptureError(hint.originalException)) {
    console.log("[Sentry] error not captured", hint.originalException)
    return null
  }

  return event;
}

function shouldCreateSpanForRequest(url: string): boolean {
  return !TRACE_IGNORE.some(expr => expr.test(url))
}

function shouldCaptureError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    if (!error.response) return true  // usually a network error.  we'll capture these too for now
    if ([419, 401].includes(error.response.status)) return false // usually means session timeout
  }
  return true;
}

function addAxiosSentryInterceptor() {
  axios.interceptors.response.use(response => response, handleAxiosError)
}

// type is unknown because axios types are super loose, and the error handler has type any => any
function handleAxiosError(error: unknown): Promise<unknown> {
  if (!(error instanceof AxiosError)) {
    Sentry.captureException(error)
    return Promise.reject(error)
  }
  if ([401, 419, 422].includes(error.response?.status ?? 0)) {
    // 401 and 419 are from session expiration (419 comes from CSRF validation failure, uncommon in our app)
    // 422 is validation failure, which is expected, but it _will_ be logged by the top-level handler if not caught
    return Promise.reject(error)
  }
  Sentry.captureException(error)
  return Promise.reject(error)
}

